import { FC, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';

import { FieldContainerIw } from '@/_pages/interwill/components/FieldContainerIw';
import { ActivationStatus } from '@/entities/vfo/types';
import { Checkbox } from '@/features/vfo/components/Checkbox';
import { UserCell } from '@/features/vfo/components/UserCell';
import { CASE_BACKGROUND_STYLES, CaseBackground } from '@/features/vfo/constants';
import { useCases } from '@/features/vfo/hooks/useCases';
import { useChat } from '@/features/vfo/hooks/useChat';
import { useFilteredMembers } from '@/features/vfo/hooks/useFilteredMembers';
import { useMembers } from '@/features/vfo/hooks/useMembers';
import { FormItem } from '@/features/vfo/uikit/Form';
import { TextArea } from '@/features/vfo/uikit/TextArea';
import { ApiCaseResponseV2 } from '@/shared/api';
import { Input } from '@/shared/components';
import { useAppRouter } from '@/shared/hooks/app/useAppRouter';
import { Icon } from '@/shared/uikit/Icon';
import { Button } from '@/shared/uikitV2/Button';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { AddOrEditCaseModalProps } from '@/widgets/modals/types';

import { ModalContainerV1 } from '../../interwill/containers/ModalContainerV1';

import {
  Buttons,
  CasePreview,
  Container,
  Fields,
  ListContainer,
  ListItem,
  PreviewWrapper,
  RadioStyled,
} from './styled';

type FormParams = {
  title: string;
  description: string;
  preview: ApiCaseResponseV2['background'];
  members?: number[];
};

export const AddNewCaseModal: FC<AddOrEditCaseModalProps> = ({
  caseId,
  hidePreview,
  openChat = true,
  preselectedMember,
}) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'addOrEditCase' });
  const modals = useModalsStore();

  const [tab, setTab] = useState(0);

  const router = useAppRouter();
  const { membersWithoutCurrentUser, founder, currentUser } = useMembers();
  const { createCase, updateCase, cases, addMember, removeMember } = useCases();
  const { fetchChats, selectChat } = useChat();

  const caseData = caseId ? cases.find((c) => c.id === caseId) : undefined;

  const getDefaultMembers = () => {
    if (caseData) {
      return (
        caseData.members.map((m) => m.profileId).filter((id): id is number => id !== undefined) ??
        []
      );
    }

    const defaultIds = new Set<number>();

    if (founder?.profileId && currentUser?.profileId !== founder.profileId) {
      defaultIds.add(founder.profileId);
    }

    if (preselectedMember) {
      defaultIds.add(preselectedMember);
    }

    return Array.from(defaultIds);
  };

  const {
    control,
    handleSubmit: formHandleSubmit,
    formState: { isSubmitting },
  } = useForm<FormParams>({
    defaultValues: {
      title: caseData?.title ?? '',
      description: caseData?.description ?? '',
      preview: caseData?.background ?? CaseBackground.General,
      members: getDefaultMembers(),
    },
  });

  const activeMembers = membersWithoutCurrentUser.filter(
    (item) =>
      item.profileId !== undefined &&
      !item.isFounder &&
      item.activationStatus === ActivationStatus.ACTIVE,
  );

  const availableMembers = useFilteredMembers(activeMembers);

  const backgrounds = [
    CaseBackground.General,
    CaseBackground.Briefcase,
    CaseBackground.Users,
    CaseBackground.House,
    CaseBackground.Key,
    CaseBackground.Invoice,
    CaseBackground.Certificate,
    CaseBackground.Buildings,
    CaseBackground.Bank,
    CaseBackground.Globe,
  ];

  const onSubmit = async (values: FormParams) => {
    if (caseId) {
      await updateCase(caseId, {
        title: values.title,
        description: values.description,
        background: values.preview,
      });

      // Handle members update
      const currentMemberIds = caseData?.members.map((m) => m.profileId) ?? [];
      const newMemberIds = values.members ?? [];

      const toAdd = newMemberIds.filter((id) => !currentMemberIds.includes(id));
      const toRemove = currentMemberIds.filter((id) => !newMemberIds.includes(id));

      await Promise.allSettled([
        ...toAdd.map((profileId) => {
          const member = membersWithoutCurrentUser.find((m) => m.profileId === profileId);
          if (!member?.tag) return false;
          return addMember({ caseId, profileId, tag: member?.tag, country: member?.country });
        }),
        ...toRemove.map((id) => removeMember(caseId, id)),
      ]);
      fetchChats();
      modals.closeModal();
      return;
    }

    if (tab === 0) {
      setTab(1);
      return;
    }

    const newCase = await createCase({
      title: values.title,
      description: values.description,
      background: values.preview,
      memberProfileIds: values.members,
    });

    if (newCase) {
      fetchChats();
      await router.pushShallow(`/vfo/case/${newCase.id}`);
      modals.closeModal();
      modals.openModal({
        type: 'CaseCreatedModal',
        caseData: newCase,
        openChat,
      });
      if (newCase.chatId) {
        selectChat(newCase.chatId);
      }
    }
  };

  return (
    <ModalContainerV1 title={t(caseId ? 'editTitle' : 'title')}>
      <Container onSubmit={formHandleSubmit(onSubmit)}>
        {tab === 0 && (
          <Fields>
            <Controller
              name={'title'}
              control={control}
              rules={{ required: t('fields.title.error'), maxLength: 40 }}
              render={({ field, fieldState }) => {
                return (
                  <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                    <FormItem
                      validateStatus={fieldState.error ? 'error' : ''}
                      help={fieldState.error?.message}
                    >
                      <Input
                        placeholder={t(`fields.${field.name}.placeholder`)}
                        type={'text'}
                        count={{
                          max: 40,
                          show: true,
                        }}
                        {...field}
                      />
                    </FormItem>
                  </FieldContainerIw>
                );
              }}
            />
            <Controller
              name={'description'}
              control={control}
              rules={{ maxLength: 200 }}
              render={({ field, fieldState }) => {
                return (
                  <FieldContainerIw label={t(`fields.${field.name}.label`)}>
                    <FormItem
                      validateStatus={fieldState.error ? 'error' : ''}
                      help={fieldState.error?.message}
                    >
                      <TextArea
                        autoComplete="off"
                        placeholder={t(`fields.${field.name}.placeholder`)}
                        count={{
                          max: 200,
                          show: true,
                        }}
                        {...field}
                      />
                    </FormItem>
                  </FieldContainerIw>
                );
              }}
            />
            {!hidePreview && (
              <Controller
                name={'preview'}
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => {
                  return (
                    <FieldContainerIw label={t(`fields.${field.name}.label`)}>
                      <FormItem
                        validateStatus={fieldState.error ? 'error' : ''}
                        help={fieldState.error?.message}
                      >
                        <PreviewWrapper>
                          {backgrounds.map((item) => {
                            const bgConfig = CASE_BACKGROUND_STYLES[item];
                            return (
                              <CasePreview key={item} style={{ background: bgConfig.background }}>
                                <Icon
                                  size="s64"
                                  name={bgConfig.icon}
                                  style={{ color: bgConfig.iconColor, opacity: 0.7 }}
                                />
                                <RadioStyled
                                  name="preview"
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      field.onChange(item);
                                    }
                                  }}
                                  checked={field.value === item}
                                />
                              </CasePreview>
                            );
                          })}
                        </PreviewWrapper>
                      </FormItem>
                    </FieldContainerIw>
                  );
                }}
              />
            )}
          </Fields>
        )}

        {tab === 1 && !availableMembers.length && <Fields>{t('noMembers')}</Fields>}

        {tab === 1 && availableMembers.length > 0 && (
          <Fields>
            {currentUser?.profileId !== founder?.profileId && founder && (
              <>
                <span>{t('founder')}</span>
                <ListItem key={founder.profileId}>
                  <Checkbox checked={true} disabled />
                  <UserCell member={founder} />
                </ListItem>
              </>
            )}
            <span>{t('members')}</span>
            <Controller
              name={'members'}
              control={control}
              render={({ field }) => (
                <ListContainer>
                  {availableMembers.map((member) => {
                    if (!member.profileId) return null;
                    const isSelected = field.value?.includes(member.profileId);
                    return (
                      <ListItem
                        key={member.profileId}
                        onClick={() => {
                          const currentValue = field.value ?? [];
                          if (isSelected) {
                            field.onChange(currentValue.filter((id) => id !== member.profileId));
                          } else {
                            field.onChange([...currentValue, member.profileId]);
                          }
                        }}
                      >
                        <Checkbox checked={isSelected} />
                        <UserCell member={member} />
                      </ListItem>
                    );
                  })}
                </ListContainer>
              )}
            />
          </Fields>
        )}
        <Buttons>
          <Button size="middle" type="default" onClick={modals.closeModal}>
            {t('cancelBtn')}
          </Button>
          {!caseId && (
            <Button htmlType="submit" size="middle" disabled={isSubmitting} loading={isSubmitting}>
              {t(tab === 0 ? 'nextBtn' : 'createBtn')}
            </Button>
          )}
          {caseId && (
            <Button htmlType="submit" size="middle">
              {t('saveBtn')}
            </Button>
          )}
        </Buttons>
      </Container>
    </ModalContainerV1>
  );
};
